/* Reusable voice-note player.
   Props:
     note: { id, sender_name, recipient_name, duration_seconds, content_type, created_at, read }
     side: 'in' | 'out'   (incoming or outgoing — affects bubble alignment + colour)
     onPlayed?: () => void
*/
import React, { useEffect, useRef, useState } from 'react';
import { Play, Pause, Headphones } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;

function authHeader() {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
}

function fmtTime(s) {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export default function VoiceNotePlayer({ note, side = 'in', onPlayed }) {
  const [audioUrl, setAudioUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const audioRef = useRef(null);

  const fetchAudio = async () => {
    if (audioUrl) return audioUrl;
    setLoading(true); setError('');
    try {
      const res = await fetch(`${BASE}/api/codex/voice/${note.id}/audio`, { headers: { ...authHeader() } });
      if (!res.ok) throw new Error('Audio unavailable');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setAudioUrl(url);
      return url;
    } catch (e) {
      setError(e.message);
      return null;
    } finally { setLoading(false); }
  };

  const toggle = async () => {
    let url = audioUrl;
    if (!url) url = await fetchAudio();
    if (!url || !audioRef.current) return;
    if (audioRef.current.paused) {
      audioRef.current.play();
      if (!note.read && onPlayed) onPlayed();
    } else {
      audioRef.current.pause();
    }
  };

  useEffect(() => () => { if (audioUrl) URL.revokeObjectURL(audioUrl); }, [audioUrl]);

  const isOut = side === 'out';

  return (
    <div
      data-testid={`voice-note-${note.id}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '10px 14px',
        borderRadius: 14,
        background: isOut
          ? 'linear-gradient(135deg, rgba(212,175,55,0.18), rgba(234,88,12,0.12))'
          : 'linear-gradient(135deg, rgba(168,85,247,0.18), rgba(11,10,22,0.4))',
        border: `1px solid ${isOut ? 'rgba(212,175,55,0.35)' : 'rgba(168,85,247,0.35)'}`,
        maxWidth: 320,
        marginLeft: isOut ? 'auto' : undefined,
      }}
    >
      <button
        type="button"
        onClick={toggle}
        data-testid={`voice-note-play-${note.id}`}
        style={{
          width: 36, height: 36, borderRadius: '50%',
          background: isOut ? 'var(--codex-gold)' : 'var(--codex-purple-light)',
          color: '#1a0e2c', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {loading ? <Headphones size={14} /> : (playing ? <Pause size={14} /> : <Play size={14} />)}
      </button>

      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 11, color: isOut ? 'var(--codex-gold-light)' : '#c4b5fd', letterSpacing: '0.1em', fontFamily: "'Cinzel', serif" }}>
          {isOut ? 'YOU' : (note.sender_name || 'MESSAGE').toUpperCase()}
        </div>
        <div style={{
          height: 4, borderRadius: 999, marginTop: 6,
          background: 'rgba(0,0,0,0.4)', overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', width: `${progress}%`,
            background: isOut ? 'var(--codex-gold-light)' : 'var(--codex-purple-light)',
            transition: 'width 0.15s linear',
          }} />
        </div>
        <div style={{ fontSize: 11, opacity: 0.75, marginTop: 4 }}>
          {fmtTime(note.duration_seconds || 0)}
          {error && <span style={{ marginLeft: 6, color: '#fca5a5' }}>· {error}</span>}
        </div>
      </div>

      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => { setPlaying(false); setProgress(100); setTimeout(() => setProgress(0), 600); }}
          onTimeUpdate={(e) => {
            const t = e.currentTarget;
            if (t.duration) setProgress((t.currentTime / t.duration) * 100);
          }}
          style={{ display: 'none' }}
        />
      )}
    </div>
  );
}
